import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Paper,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link as RouterLink } from "react-router-dom";

import { resetPasswordUi } from "../../../api/ui";
import Header from "../../../components/header";

export function AssinaturasResetPassword(): React.ReactElement {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const codeParam = searchParams.get("code") || "";

  const [code, setCode] = useState(codeParam);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!code) {
      setError(t("assinaturas.resetPassword.codeRequiredError"));
      return;
    }

    if (newPassword.length < 6) {
      setError(t("assinaturas.resetPassword.minPasswordLengthError"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("assinaturas.resetPassword.passwordsMismatchError"));
      return;
    }

    setLoading(true);

    try {
      await resetPasswordUi(code, newPassword);
      setSuccess(true);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setError(typeof msg === "string" ? msg : t("assinaturas.resetPassword.resetError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f8fafc" }}>
      <Header />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 420,
            width: "100%",
            borderRadius: 3,
            p: 4,
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)",
            bgcolor: "#ffffff",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                bgcolor: "#eeebfc",
                color: "#2F16C9",
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography color="#1e293b" component="h1" fontWeight="bold" gutterBottom variant="h5">
              {t("assinaturas.resetPassword.title")}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t("assinaturas.resetPassword.subtitle")}
            </Typography>
          </Box>

          {success ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {t("assinaturas.resetPassword.successMessage")}
              </Alert>
              <Button
                onClick={() => { navigate("/assinaturas/login"); }}
                sx={{ bgcolor: "#2F16C9", fontWeight: "bold", borderRadius: 2, "&:hover": { bgcolor: "#0C1C69" } }}
                variant="contained"
              >
                {t("assinaturas.resetPassword.goToLogin")}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={(e) => { void handleSubmit(e); }}>
              {error ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              ) : null}

              <TextField
                fullWidth
                label={t("assinaturas.resetPassword.codeLabel")}
                margin="normal"
                onChange={(e) => { setCode(e.target.value); }}
                required
                value={code}
              />

              <TextField
                fullWidth
                label={t("assinaturas.resetPassword.newPasswordLabel")}
                margin="normal"
                onChange={(e) => { setNewPassword(e.target.value); }}
                required
                type="password"
                value={newPassword}
              />

              <TextField
                fullWidth
                label={t("assinaturas.resetPassword.confirmPasswordLabel")}
                margin="normal"
                onChange={(e) => { setConfirmPassword(e.target.value); }}
                required
                type="password"
                value={confirmPassword}
              />

              <Button
                disabled={loading}
                fullWidth
                sx={{
                  py: 1.5,
                  mt: 2,
                  bgcolor: "#2F16C9",
                  fontWeight: "bold",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px 0 rgba(47, 22, 201, 0.2)",
                  "&:hover": { bgcolor: "#0C1C69" },
                }}
                type="submit"
                variant="contained"
              >
                {loading ? <CircularProgress color="inherit" size={24} /> : t("assinaturas.resetPassword.submitButton")}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2.5 }}>
                <Link component={RouterLink} sx={{ color: "#2F16C9" }} to="/assinaturas/login" underline="hover" variant="body2">
                  {t("assinaturas.resetPassword.backToLogin")}
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AssinaturasResetPassword;
